import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './FAQSection.css';

export interface FAQItem {
  question: string;
  answer: string;
  category?: 'general' | 'cleaning' | 'ndis' | 'commercial';
}

export const faqData: FAQItem[] = [
  // General/Company Information
  {
    question: 'What is TaskWombat\'s ABN and ACN?',
    answer: 'TaskWombat is a fully registered Australian business. For our ABN (Australian Business Number) and ACN (Australian Company Number), please contact our customer service team. We are a legitimate, registered business providing professional commercial and residential cleaning services across Australia with full compliance and transparency.',
    category: 'general',
  },
  {
    question: 'Are your services insured?',
    answer: 'Yes! TaskWombat provides comprehensive public liability insurance coverage up to $10 million for all our services. Whether it\'s commercial or residential cleaning services, you\'re fully protected. All our team members are also individually insured and verified.',
    category: 'general',
  },
  {
    question: 'What areas does TaskWombat service?',
    answer: 'TaskWombat provides Australia\'s most trusted professional commercial and residential cleaning services across Australia. We serve major cities and regions including Victoria, New South Wales, Queensland, and more. We\'re continuously expanding our coverage to serve more areas across Australia.',
    category: 'general',
  },
  {
    question: 'How do I book a service?',
    answer: 'Booking is easy! Simply fill out our quote form with your service needs and property details. You\'ll receive an instant, transparent quote. Choose your preferred date and time, and our professional team will arrive ready to deliver exceptional results. Available for commercial and residential cleaning services.',
    category: 'general',
  },
  {
    question: 'Can I get a quote before booking?',
    answer: 'Yes! We provide free, instant quotes with no obligation for all services. Simply fill out our quote form with your property details and service requirements, and you\'ll receive a transparent quote immediately. No hidden fees, no surprises.',
    category: 'general',
  },
  {
    question: 'What if I\'m not satisfied with the service?',
    answer: 'We offer a 100% satisfaction guarantee on all services. If you\'re not completely satisfied with any commercial or residential cleaning service, simply contact us within 24 hours and we\'ll return to fix it at no extra cost. Your satisfaction is our top priority.',
    category: 'general',
  },
  
  
  // Cleaning Services
  {
    question: 'What types of cleaning services do you offer?',
    answer: 'We offer comprehensive cleaning services including: Regular Home Cleaning, Deep Cleaning, End of Lease Cleaning, Bond Cleaning, Carpet Cleaning, Window Cleaning, Oven Cleaning, Commercial Cleaning, Office Cleaning, Airbnb Cleaning, Move-In/Move-Out Cleaning, and Post-Construction Cleaning. All services use eco-friendly products.',
    category: 'cleaning',
  },
  {
    question: 'How much does professional cleaning cost?',
    answer: 'Cleaning costs vary based on property size, type of cleaning required, and location. We provide free, instant quotes with transparent pricing - no hidden fees. Prices start from competitive rates for regular cleaning services. Get your personalized quote today to see exact pricing for your needs.',
    category: 'cleaning',
  },
  {
    question: 'What is included in end of lease cleaning?',
    answer: 'Our comprehensive end of lease cleaning includes: inside oven and stove deep clean, inside fridge and freezer sanitization, all windows (inside and outside), cupboards and drawers cleaned, skirting boards and cornices, light fixtures and exhaust fans, behind all appliances, inside wardrobes and storage, bathroom deep sanitization, carpet cleaning (if required), and bond-back guarantee. If the property doesn\'t pass inspection, we\'ll return to fix it at no extra cost.',
    category: 'cleaning',
  },
  {
    question: 'What cleaning products do you use?',
    answer: 'TaskWombat uses eco-friendly, non-toxic cleaning products that are safe for your family, pets, and the environment. Our professional cleaners use proven techniques and premium eco-friendly products to deliver exceptional results while being environmentally responsible. All products are pet and child-safe.',
    category: 'cleaning',
  },
  {
    question: 'Do you offer regular/recurring cleaning services?',
    answer: 'Yes! We offer flexible regular cleaning services including weekly, fortnightly (bi-weekly), and monthly schedules. Regular cleaning helps maintain a consistently clean home and can be customized to fit your lifestyle and budget. Many of our customers enjoy the convenience of regular maintenance cleaning.',
    category: 'cleaning',
  },
  {
    question: 'Do you clean Airbnb properties?',
    answer: 'Absolutely! We specialize in Airbnb and short-stay rental cleaning. Our fast turnaround service (2-4 hours) ensures your property is guest-ready quickly. We handle linen and towel changes, thorough sanitization, bed making, and photo-ready presentation - perfect for Airbnb hosts, Stayz, Booking.com properties, and other short-stay rentals.',
    category: 'cleaning',
  },
  {
    question: 'Do you provide commercial cleaning services?',
    answer: 'Yes, we provide professional commercial cleaning services for offices, retail spaces, medical facilities, and commercial properties. We offer customized cleaning schedules to meet your business needs with minimal disruption to your operations. All commercial cleaners are fully insured and trained.',
    category: 'cleaning',
  },
  
  // Commercial/Contract Cleaning Services
  {
    question: 'Do you offer long-term commercial cleaning contracts?',
    answer: 'Yes! We specialize in long-term commercial cleaning contracts for property managers, facility managers, and commercial operators. Our contract cleaning services include fixed monthly pricing, scheduled service, quality reporting, and ongoing quality checks. Perfect for offices, strata properties, medical facilities, childcare centers, hospitality venues, and retail spaces.',
    category: 'commercial',
  },
  {
    question: 'What commercial cleaning services do you provide?',
    answer: 'We provide comprehensive commercial cleaning services including: Office Cleaning, Strata & Apartment Common Areas, Medical / Allied Health Clinics, Childcare & Education Facilities, Hospitality & Venues, and Retail & Showrooms. All services are tailored to your facility\'s specific needs with consistent scheduling and quality assurance.',
    category: 'commercial',
  },
  {
    question: 'Are your commercial cleaners insured and compliant?',
    answer: 'Absolutely! All our commercial cleaning staff are fully insured with $10M public liability insurance, police checked, and OH&S compliant. We meet all procurement requirements including Working with Children Checks for childcare facilities, Safe Work compliance, and consistent quality reporting. Property managers trust us because we meet all their compliance requirements.',
    category: 'commercial',
  },
  {
    question: 'How does commercial contract pricing work?',
    answer: 'Commercial contract pricing is based on a site inspection where we assess your facility\'s size, cleaning requirements, and frequency needs. We provide fixed monthly pricing with no hidden fees. Long-term contracts offer better value and include scheduled service, quality checks, and reporting. Contact us for a site walkthrough to receive an accurate quote.',
    category: 'commercial',
  },
  {
    question: 'What is included in a commercial cleaning contract?',
    answer: 'Our commercial cleaning contracts include: Site inspection and scope definition, fixed monthly contract pricing, ongoing scheduled service (daily, weekly, bi-weekly, or monthly), quality inspections and reporting, consistent cleaning schedules, and access to our management team. We work with property managers and facility managers to ensure all requirements are met.',
    category: 'commercial',
  },
  {
    question: 'Do you work with property managers and strata companies?',
    answer: 'Yes! We work extensively with property managers, strata management companies, and facility management companies. We understand the unique requirements of managed properties including common area cleaning, compliance reporting, and scheduled maintenance. Our services are designed to meet procurement requirements and provide reliable, consistent cleaning for strata properties.',
    category: 'commercial',
  },
  {
    question: 'Can you provide cleaning for medical and healthcare facilities?',
    answer: 'Yes! We provide specialized cleaning services for medical facilities, allied health clinics, and healthcare providers. Our staff are trained in healthcare cleaning protocols, use appropriate sanitization methods, and maintain compliance with healthcare facility requirements. All staff have Working with Children Checks and police clearances.',
    category: 'commercial',
  },
  {
    question: 'Do you offer scheduled cleaning for hospitality venues?',
    answer: 'Absolutely! We provide scheduled cleaning services for hotels, short-stay accommodation, restaurants, and event venues. Our hospitality cleaning services include regular maintenance, deep cleaning, and specialized cleaning protocols. We work around your business hours to minimize disruption and maintain high standards for guest satisfaction.',
    category: 'commercial',
  },
  {
    question: 'What cleaning schedules are available for commercial contracts?',
    answer: 'We offer flexible cleaning schedules to meet your business needs: Daily cleaning, Weekly cleaning, Bi-weekly (every 2 weeks), Monthly cleaning, and Custom schedules. During the site inspection, we\'ll recommend the best schedule for your facility type and traffic levels. All schedules include quality checks and reporting.',
    category: 'commercial',
  },
  {
    question: 'How do I request a commercial cleaning contract quote?',
    answer: 'To request a commercial cleaning contract quote, fill out our quote form and select "Commercial/Contract" option. Provide your business name, facility type, site address, and preferred cleaning schedule. We\'ll review your requirements and contact you within 24 hours to arrange a site inspection. After the inspection, you\'ll receive a detailed contract quote with fixed monthly pricing.',
    category: 'commercial',
  },
  {
    question: 'Do you offer carpet cleaning?',
    answer: 'Yes! We provide professional carpet cleaning services using steam cleaning and dry cleaning methods. Our carpet cleaning service removes deep-seated dirt, stains, and allergens, leaving your carpets fresh and sanitized. Perfect for regular maintenance or before moving in/out.',
    category: 'cleaning',
  },
  {
    question: 'Do you offer window cleaning?',
    answer: 'Yes! We provide professional window cleaning services for both interior and exterior windows. Our window cleaning service includes window frames, sills, and tracks, leaving your windows streak-free and crystal clear. Available for residential and commercial properties.',
    category: 'cleaning',
  },
  {
    question: 'Do you offer same-day cleaning services?',
    answer: 'Yes, we offer same-day cleaning services subject to availability. Simply request a quote and select your preferred date. Same-day service is popular, so we recommend booking as early as possible to secure your preferred time slot. Available for most cleaning services.',
    category: 'cleaning',
  },
  {
    question: 'Are your cleaners insured and verified?',
    answer: 'Yes! All TaskWombat cleaners are fully verified, background-checked, and insured. We provide comprehensive public liability insurance coverage up to $10 million, giving you complete peace of mind. Every cleaner goes through our rigorous verification process before joining our platform.',
    category: 'cleaning',
  },
  
  
  // NDIS Services
  {
    question: 'Do you provide NDIS services?',
    answer: 'Yes! TaskWombat is a registered NDIS provider offering professional cleaning services for NDIS participants. We understand the unique needs of NDIS participants and provide flexible, person-centered services that support independence and well-being.',
    category: 'ndis',
  },
  {
    question: 'What NDIS services do you offer?',
    answer: 'We offer NDIS-approved services including: Home Cleaning Services, Lawn Care and Garden Maintenance, Home Maintenance Support, and Assistance with Daily Living tasks. All services are tailored to meet individual NDIS plan requirements and goals.',
    category: 'ndis',
  },
  {
    question: 'How do I book NDIS services?',
    answer: 'To book NDIS services, contact us with your NDIS plan details and service requirements. We\'ll work with you, your support coordinator, or plan manager to ensure services align with your NDIS plan. We provide transparent quotes and flexible scheduling to meet your needs.',
    category: 'ndis',
  },
  {
    question: 'Are your NDIS services covered by NDIS funding?',
    answer: 'Yes, our NDIS services can be funded through your NDIS plan, depending on your plan type and funding categories. We work with self-managed, plan-managed, and NDIA-managed participants. Contact us to discuss how our services fit into your NDIS plan.',
    category: 'ndis',
  },
  {
    question: 'Do your staff have NDIS worker screening checks?',
    answer: 'Yes! All TaskWombat staff providing NDIS services have completed NDIS worker screening checks and hold valid Working with Children Checks (WWCC) where required. We maintain the highest standards of safety and compliance for NDIS participants.',
    category: 'ndis',
  },
];

const FAQSection: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('general');

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const categories = [
    { id: 'general', name: 'General', icon: 'info' },
    { id: 'commercial', name: 'Commercial', icon: 'business' },
    { id: 'cleaning', name: 'Cleaning', icon: 'cleaning_services' },
    { id: 'ndis', name: 'NDIS', icon: 'accessibility_new' },
  ];

  const filteredFAQs = faqData
    .filter(faq => faq.category === selectedCategory)
    .slice(0, 5); // Limit to 5 questions per category

  return (
    <section className="faq-section" aria-label="Frequently Asked Questions">
        <div className="faq-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="faq-header"
          >
            <h2>Frequently Asked Questions</h2>
            <p>Find answers to common questions about TaskWombat's professional commercial and residential cleaning services</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="faq-categories"
          >
            {categories.map((category) => (
              <button
                key={category.id}
                className={`faq-category-btn ${selectedCategory === category.id ? 'active' : ''}`}
                onClick={() => {
                  setSelectedCategory(category.id);
                  setOpenIndex(null);
                }}
              >
                <span className="material-icons">{category.icon}</span>
                <span>{category.name}</span>
              </button>
            ))}
          </motion.div>

          <div className="faq-content-wrapper">
            <div className="faq-list">
              {filteredFAQs.map((faq, index) => {
                const originalIndex = faqData.findIndex(f => f.question === faq.question);
                return (
                  <motion.div
                    key={`${faq.category}-${originalIndex}`}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="faq-item"
                  >
                    <button
                      className={`faq-question ${openIndex === originalIndex ? 'open' : ''}`}
                      onClick={() => toggleFAQ(originalIndex)}
                      aria-expanded={openIndex === originalIndex}
                      aria-controls={`faq-answer-${originalIndex}`}
                    >
                      <span className="faq-question-text">{faq.question}</span>
                      <span className="faq-icon">
                        <span className="material-icons">
                          {openIndex === originalIndex ? 'expand_less' : 'expand_more'}
                        </span>
                      </span>
                    </button>
                    <AnimatePresence>
                      {openIndex === originalIndex && (
                        <motion.div
                          id={`faq-answer-${originalIndex}`}
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="faq-answer"
                        >
                          <p>{faq.answer}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="faq-image-container"
            >
              <img
                src="/mecanic.png"
                alt="TaskWombat professional service provider"
                className="faq-mecanic-image"
              />
            </motion.div>
          </div>
        </div>
      </section>
  );
};

export default FAQSection;
